import { makeStyles, Snackbar, styled } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useCollection, useIO, useLinked } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, PrimaryButton, div, navigate } from "lqx-react";
import React from "react";
import Select from "react-select";
import { TargetFieldCard } from "../../api/targetFieldCard";
import { RuleItem, ValidationRuleDetails } from "../../api/validationRuleDetails";
import {
  BackButton, CancelButton, ConfirmationButton,
  DigiCheckbox,
  RightAlignedButtons, StartOverButton,
  WorkflowFooter, WorkflowTitle
} from "../../shared";
import { Alert, AlertTitle } from "@material-ui/lab";


export const ValidationRuleDefineStep = ({ next, back, cancel, startOver, isEdit, validationRuleDetails }
  : { next?, back?, cancel?, startOver?, isEdit?, validationRuleDetails?: ValidationRuleDetails }) => {

  const classes = useStyles();
  const targetFieldCards = useCollection.of(TargetFieldCard);
  const standardRuleSet = useLinked([]);

  const isReady = useIO(async () => {
    await targetFieldCards.fetch({ params: { assetTypeId: validationRuleDetails.assetTypeId } });
    const requiredRullSets = targetFieldCards.models
      .filter(x => x.isRequired)
      .map((item) => ({ isRequired: true, fieldName: item.name, fieldId: +item.id, id: `${item.id}` }));
    
    if ((!isEdit && validationRuleDetails.ruleSets.length === 0) || 
        (isEdit && validationRuleDetails.ruleSets.length === 0)) {
      validationRuleDetails.ruleSets.set(requiredRullSets);
    }

    standardRuleSet.set(requiredRullSets);
  }, [validationRuleDetails.assetTypeId]);

  const addValidationField = () => {
    const newField = { isRequired: false, fieldName: null, fieldId: 0, id: `0` } as RuleItem
    validationRuleDetails.ruleSets.set([...validationRuleDetails.ruleSets.models, newField]);
  }

  const onDeleteValidationField = (id: string) => {
    validationRuleDetails.ruleSets.remove(id);
  }

  const $showErrors = useLinked(false);
  const $errorMessage = useLinked("Error happened!");
  const $disableSave = useLinked(false);

  const handleCloseShowErrors = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    $showErrors.set(false);
    $errorMessage.set("Error happened!");
  };

  const save = () =>
    validationRuleDetails
      .save()
      .then(() => navigate('validation-rules'))
      .catch(err => {
        $showErrors.set(true);
        $errorMessage.set(err.message);
        $disableSave.set(false);
        return;
      });

  return (isReady ?
    <>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={6000} onClose={handleCloseShowErrors}>
        <Alert severity="error">
          <AlertTitle>Error during saving</AlertTitle>
          {$errorMessage.value}
        </Alert>
      </Snackbar>
      <WorkflowTitle>
        Step 2: Define your validation Requirements
      </WorkflowTitle>
      <ValidationBodyContainer>
        <FlexBody row>
          <MainContainer>
            <PlateHeader>
              <Column>
                <span>Validation Fields</span>
              </Column>
            </PlateHeader>
            <PlateContainer>
              <Flex row>
                <Column style={{ alignItems: 'flex-end' }}>
                  <Button style={{ width: '140px' }} onClick={addValidationField} color="primary">Add New Field</Button>
                </Column>
              </Flex>
              <Flex row>
                <RequiredFieldColumn>Required</RequiredFieldColumn>
                <FieldNameColumn>Name</FieldNameColumn>
              </Flex>
              {
                validationRuleDetails.ruleSets.map((item, index) => {
                  return (
                    <FieldRow
                      data-qa-id={`inBlockTargetFields${index}`}>
                      <CheckboxCol>
                        <DigiCheckbox
                          $checked={item.$.isRequired}
                          readOnly={standardRuleSet.value.some(t => t.fieldId === item.fieldId)} />
                      </CheckboxCol>
                      <FieldNameColumn>
                        <Select
                          isMulti={false}
                          //placeholder={'Field Name'}
                          styles={customDropDownStyles}
                          value={targetFieldCards.models.filter((option) => option.name === item.fieldName)}
                          isDisabled={standardRuleSet.value.some(t => t.fieldId === item.fieldId)}
                          getOptionValue={(e: any) => e.id}
                          getOptionLabel={(e: any) => e.name}
                          isOptionDisabled={(e: any) => e.disabled}
                          options={targetFieldCards.models.filter(t => !validationRuleDetails.ruleSets.models.find(r => r.fieldName === t.name))}
                          onChange={(value: any) => { item.$.fieldName.set(value.name); item.$.fieldId.set(value.id.toString()); item.$.id.set(value.id.toString()); }}
                        />
                      </FieldNameColumn>
                      <StandardLabelColumn>
                        {standardRuleSet.value.some(t => t.fieldId === item.fieldId) ? <label>Standard Required Field</label> : null}
                      </StandardLabelColumn>
                      <button
                        style={{ border: 'none', outline: 'none', background: 'none' }}
                        disabled={standardRuleSet.value.some(t => t.fieldId === item.fieldId)}>
                        <CloseIcon
                          onClick={() => onDeleteValidationField(item.id)}
                        />
                      </button>
                    </FieldRow>
                  )
                })
              }
            </PlateContainer>
          </MainContainer>
          <MainContainer>
            <PlateHeader>
              <Column>
                <span>Validation Sets</span>
              </Column>
            </PlateHeader>
            <PlateContainer>
              Coming soon
            </PlateContainer>
          </MainContainer>
        </FlexBody>
      </ValidationBodyContainer>
      <WorkflowFooter>
        <Columns>
          <Column>
            <Flex row>
              <CancelButton color="primary" onClick={cancel}>Cancel</CancelButton>
              <StartOverButton color="primary" onClick={startOver}>Start Over</StartOverButton>
            </Flex>
          </Column>
          <Column>
            <RightAlignedButtons>
              <BackButton color="primary" onClick={back}>Back</BackButton>
              {/* <ConfirmationButton
                disabled={false}
                onClick={() => next()}>
                Next: Duplication Checks
              </ConfirmationButton> */}
              <ConfirmationButton disabled={$disableSave.value} onClick={() => {
                $disableSave.set(true);
                save();
              }}>Save Validation Sets</ConfirmationButton>
            </RightAlignedButtons>
          </Column>
        </Columns>
      </WorkflowFooter>
    </> : <Loading />);
};

export const AddButton = styled(PrimaryButton)({
  marginBottom: '10px',
  border: '0px',
  color: 'white',
  borderRadius: '10px',
  height: '25px',
  width: '100px'
});

const FlexBody = styled(Flex)({
  overflow: 'auto',
  flex: '1 1 auto'
})

const ValidationBodyContainer = div({
  display: 'flex',
  marginTop: '15px',
  justifyContent: 'center',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'stretch',
  minHeight: 'calc(100% - 220px)',
  maxHeight: 'calc(100% - 220px)',
  paddingBottom: '25px',
  overflow: 'auto'
});

const MainContainer = styled(Column)({
  flex: '1 0 450px',
  overflow: 'auto',
  borderLeft: '1px solid whitesmoke',
  background: 'white'
});

const PlateHeader = div({
  display: 'Flex',
  height: '50px',
  background: '#052C4C',
  width: 'auto',
  alignItems: 'center',
  color: 'white'
});

const PlateContainer = div({
  borderRadius: '4px',
  padding: '8px',
  justifyContent: 'center',
  maxHeight: 'calc(100% - 50px)'
});

const BaseColumn = styled(Column)({
  color: '#668AA5',
  fontSize: '14px',
  fontWeight: 600
})

const RequiredFieldColumn = styled(BaseColumn)({
  marginLeft: '2em',
  flex: '0.2',
  justifyContent: 'center'
})

const CheckboxCol = styled(BaseColumn)({
  marginLeft: '10px',
  flex: '0.0',
  justifyContent: 'center'
})

const FieldNameColumn = styled(BaseColumn)({
  //flex: '0.5'
})

const StandardLabelColumn = styled(BaseColumn)({
  flex: '0.5',
  color: 'red',
  justifyContent: 'center',
  fontWeight: 'normal',
  fontSize: 12
})

const customDropDownStyles = {
  container: provided => ({
    ...provided,
    width: '80%'
  }),
  control: provided => ({
    ...provided,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#E3E9ED'
  }),
  valueContainer: (provided) => ({
    ...provided,
    width: 'auto',
    overflowX: 'hidden',
    overflowY: 'hidden',
    fontSize: 14,
    fontWeight: 500,
  }),
  indicatorSeparator: _ => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.54)',
    marginRight: 0,
    padding: 0
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999, marginTop: 0 })
}

const FieldRow = div({
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: 'whitesmoke',
  marginTop: 5,
  marginBottom: 3,
  borderRadius: 5,
  padding: '10px 10px',
  fontSize: '12px',
  fontFamily: 'Montserrat',
  fontWeight: 500,
});

const CloseBtn = div({
  marginLeft: 'auto',
  alignSelf: 'center',
  height: '22px',
  cursor: 'pointer'
});

const AddIconBtn = div({
  display: 'flex',
  width: 'fit-content',
  color: '#0098DB',
  transform: 'scale(1.3)',
  margin: '10px',
  cursor: 'pointer',
  fontSize: '12px',
  gap: '5px'
});

const useStyles = makeStyles({
  actionButton: {
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'scale(1.2)',
      cursor: 'pointer'
    }
  }
});
